import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { postData } from '../../../services';
import Modal from '../../helpers/Modal';
import { ProjectPicker } from './ProjectPicker';
import { AssociatedObjectsPicker } from './AssociatedObjectsPicker';
import {
    TICKET_TYPES,
    buildTicketPayload,
    canSubmitTicket,
    ticketApiError,
} from '../../../utils/tickets';

const emptyForm = {
    ticketType: TICKET_TYPES.support,
    projectId: null,
    title: '',
    description: '',
    associatedObjects: [],
};

export const TicketForm = ({
    isOpen,
    onClose,
    onCreated,
    defaultTicketType = TICKET_TYPES.support,
    defaultProjectId = null,
}) => {
    const { token, base_url } = useAppContext();
    const [ticketType, setTicketType] = useState(emptyForm.ticketType);
    const [projectId, setProjectId] = useState(emptyForm.projectId);
    const [title, setTitle] = useState(emptyForm.title);
    const [description, setDescription] = useState(emptyForm.description);
    const [associatedObjects, setAssociatedObjects] = useState(emptyForm.associatedObjects);
    const [formError, setFormError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setTicketType(
            defaultTicketType === TICKET_TYPES.featureRequest
                ? TICKET_TYPES.featureRequest
                : TICKET_TYPES.support
        );
        setProjectId(defaultProjectId ? Number(defaultProjectId) : null);
        setTitle('');
        setDescription('');
        setAssociatedObjects([]);
        setFormError('');
        setIsSubmitting(false);
    }, [isOpen, defaultTicketType, defaultProjectId]);

    const isSupport = ticketType === TICKET_TYPES.support;
    const canSubmit = useMemo(
        () => canSubmitTicket({ ticketType, description, projectId, title }) && !isSubmitting,
        [ticketType, description, projectId, title, isSubmitting]
    );

    const handleTypeChange = (nextType) => {
        setTicketType(nextType);
        if (nextType === TICKET_TYPES.featureRequest) {
            setAssociatedObjects([]);
        }
    };

    const handleProjectChange = (nextProjectId) => {
        setProjectId(nextProjectId);
        setAssociatedObjects([]);
    };

    const closeForm = () => {
        if (isSubmitting) return;
        onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) return;

        setIsSubmitting(true);
        setFormError('');

        const payload = buildTicketPayload({
            ticketType,
            description,
            projectId,
            title,
            associatedObjects: isSupport ? associatedObjects : [],
        });

        try {
            const result = await postData(token, `${base_url}/apis/admin/tickets`, payload);
            const status = result?.httpStatus;

            if (status === 201 && result?.ticket) {
                if (window.showAlert) {
                    window.showAlert(201, result.msg || 'Submission created successfully');
                }
                onCreated?.(result.ticket);
                onClose();
                return;
            }

            const message = ticketApiError(result, 'Failed to create submission');
            setFormError(message);
            if (window.showAlert) {
                window.showAlert(status || 400, message);
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
            const message = error?.msg || error?.message || 'Failed to create submission. Please try again.';
            setFormError(message);
            if (window.showAlert) {
                window.showAlert(error?.status || 500, message);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const form = (
        <div className="ticket-form">
            <h2 className="modal-title modal-title--compact">New ticket</h2>
            <form onSubmit={handleSubmit} className="company-form-grid ticket-form__grid">
                <div className="form-group">
                    <span className="form-label">Type</span>
                    <div className="ticket-type-toggle" role="group" aria-label="Ticket type">
                        <button
                            type="button"
                            className={`ticket-type-toggle__btn${isSupport ? ' ticket-type-toggle__btn--active' : ''}`}
                            onClick={() => handleTypeChange(TICKET_TYPES.support)}
                            disabled={isSubmitting}
                        >
                            Support ticket
                        </button>
                        <button
                            type="button"
                            className={`ticket-type-toggle__btn${!isSupport ? ' ticket-type-toggle__btn--active' : ''}`}
                            onClick={() => handleTypeChange(TICKET_TYPES.featureRequest)}
                            disabled={isSubmitting}
                        >
                            Feature request
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="ticket-title" className="form-label ticket-form__label-row">
                        <span>Title{isSupport ? '' : ' (recommended)'}</span>
                        <span className="ticket-form__char-count">{title.length}/500</span>
                    </label>
                    <input
                        id="ticket-title"
                        type="text"
                        className="form-input"
                        maxLength={500}
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        disabled={isSubmitting}
                        placeholder={isSupport ? 'Optional summary' : 'Short name for this request'}
                    />
                </div>

                <div className={`form-row ticket-form__row${isSupport ? '' : ' ticket-form__row--single'}`}>
                    <div className="form-group">
                        <label htmlFor="ticket-project" className="form-label">
                            Project{isSupport ? ' *' : ' (optional)'}
                        </label>
                        <ProjectPicker
                            projectId={projectId}
                            onChange={handleProjectChange}
                            required={isSupport}
                            disabled={isSubmitting}
                        />
                    </div>

                    {isSupport && (
                        <div className="form-group">
                            <label htmlFor="ticket-associated-object" className="form-label">
                                Related objects
                            </label>
                            <AssociatedObjectsPicker
                                projectId={projectId}
                                selectedObjects={associatedObjects}
                                onChange={setAssociatedObjects}
                                disabled={isSubmitting || !projectId}
                            />
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="ticket-description" className="form-label">
                        Description *
                    </label>
                    <textarea
                        id="ticket-description"
                        className="form-textarea ticket-form__description"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        disabled={isSubmitting}
                        rows={4}
                        placeholder={isSupport ? 'What went wrong, and where?' : 'What should we add or change?'}
                        required
                    />
                </div>

                {formError && <p className="form-error">{formError}</p>}

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeForm}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Submitting
                            </>
                        ) : (
                            'Submit'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <Modal
            form={form}
            setIsModalOpen={(open) => {
                if (!open) closeForm();
            }}
            isWide
            fitContent
        />
    );
};
